import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment/moment";
import axios, { AxiosResponse } from "axios";

import { DropdownPortalMenu } from "src/app/components/DropdownPortal";
import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import { RootState } from "src/redux/reducers";
import { getDictionaryIcon } from "src/app/methods/getDictionaryTypes";
import { getSelectDataSetOptions } from "src/redux/select-data-set/selectDataSetActions";
import { DictionaryElementsFilter } from "src/app/Task/Subtask/Subtask";
import { API_URLS } from "src/utils/API_URLS";
import { DictionaryList, Row } from "src/redux/dictionary/types";
import responseCodes from "src/utils/responseCodes";
import { tableDataType } from "src/app/components/Table/Table";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Tooltip from "src/app/components/Tooltip/Tooltip";
import { SelectDictionaryOption } from "./DictionaryElementSelector";
import { skeletonOptions } from "./utils";
import { MetaFieldType } from "../../methods/getMetaFieldTypeOptions";
import { getDateLabel } from "../../methods/getDateLabel";
import {
  getFullDate,
  showErrorToast,
  splitNumbers,
  splitPercents,
} from "../../../utils/methods";
import DictionaryElementTableLabel from "./DictionaryElementTableLabel";
import DictionaryElementLabel from "./DictionaryElementLabel";

interface DictionaryElementOption {
  value: string;
  label: React.JSX.Element;
  tableLabel: React.JSX.Element;
  name: string;
  subField: string;
}

interface DictionaryElementSelectorMenuProps {
  handleChange: (newValue: SelectDictionaryOption) => void;
  onMenuClose: () => void;
  isVisible: boolean;
  forceOpen: boolean;
  selectedOption: Partial<SelectDictionaryOption>;
  wsDictionaryUuid: string | undefined;
  wsDictionarySubFieldUuid: string | null | undefined;
  wsDictionaryElementsFilter: DictionaryElementsFilter | undefined;
  rowId?: string;
  dataType?: tableDataType;
}

export default function DictionaryElementSelectorMenu({
  handleChange,
  onMenuClose,
  isVisible,
  forceOpen,
  selectedOption,
  wsDictionaryUuid,
  wsDictionarySubFieldUuid,
  wsDictionaryElementsFilter,
  rowId,
  dataType,
}: DictionaryElementSelectorMenuProps) {
  const [isGettingFilteredDictionaryList, setIsGettingFilteredDictionaryList] =
    useState(false);
  const [options, setOptions] = useState<DictionaryElementOption[]>([]);

  const {
    selectDataSetReducer: { selectDataSetList, isSelectDataSetRequestLoading },
    dictionaryReducer: {
      dictionaryAutoCompletes,
      dictionaryAutoCompletesColumns,
      dictionaryAutocompleteLoading,
      isDictionaryAutocompleteRequestLoading,
      dictionaryList,
    },
    mainReducer: { identity },
    projectReducer: { membersList },
  } = useSelector((state: RootState) => state);

  const { tasks, selectedTasks } = useSelector(
    (state: RootState) => state.taskReducer,
  );

  const dispatch = useDispatch();

  const intl = useIntl();

  const isLoading =
    dictionaryAutocompleteLoading ||
    isDictionaryAutocompleteRequestLoading ||
    isSelectDataSetRequestLoading ||
    isGettingFilteredDictionaryList;

  const foundCurrentMetaField = useMemo(() => {
    if (
      !wsDictionaryElementsFilter ||
      wsDictionaryElementsFilter.wsDictionaryMetaFieldTargetUuid === null
    )
      return undefined;

    switch (dataType) {
      case tableDataType.Dictionary: {
        const foundDictionaryTask = dictionaryList.list.find(
          (task) => task.uuid === rowId,
        );
        const foundMetaData = foundDictionaryTask?.metadata.find(
          (metaField) =>
            metaField.uuid ===
            wsDictionaryElementsFilter?.wsDictionaryMetaFieldTargetUuid,
        );
        const foundTypeFromColumn = dictionaryList.columns.find(
          (column) => column.uuid === foundMetaData?.uuid,
        )?.type;

        return {
          ...foundMetaData,
          type: foundTypeFromColumn,
        };
      }

      default: {
        const foundTask = tasks.find((task) => task.taskId === rowId);
        return foundTask?.metadata.find(
          (metaField) =>
            metaField.uuid === wsDictionaryElementsFilter?.wsTaskMetaFieldUuid,
        );
      }
    }
  }, [dataType, tasks, rowId, wsDictionaryElementsFilter, dictionaryList]);

  const getSubMetaFieldValue = (metaFieldItem: any): string | null => {
    const value = metaFieldItem.metadata?.find(
      (item: any) => item.uuid === wsDictionarySubFieldUuid,
    )?.value
      ? metaFieldItem.metadata?.find(
          (item: any) => item.uuid === wsDictionarySubFieldUuid,
        )?.value
      : null;

    const metaField =
      dictionaryAutoCompletesColumns[
        `dictionaryType_${wsDictionaryUuid}`
      ]?.find((item) => item.uuid === wsDictionarySubFieldUuid) ?? null;

    if (metaField?.type === MetaFieldType.DictionaryElement) {
      const subMetaFieldDictionaryUuid = metaField?.data?.wsDictionaryUuid;

      if (typeof subMetaFieldDictionaryUuid !== "string") return value;

      const dictionaryAutoCompletesDictionary =
        dictionaryAutoCompletes[
          `dictionaryType_${subMetaFieldDictionaryUuid}`
        ] ?? [];

      if (dictionaryAutoCompletesDictionary.length > 0) {
        const supportingFieldDictionaryItem =
          dictionaryAutoCompletesDictionary.find(
            (dictionaryItem) => dictionaryItem.uuid === value,
          );

        if (!supportingFieldDictionaryItem) return value;

        return supportingFieldDictionaryItem.title;
      }

      return value;
    }

    if (metaField?.type === MetaFieldType.SingleSelect) {
      if (metaField.data?.wsSelectDataSetUuid) {
        const foundDataset =
          selectDataSetList[
            `dataSetType_${metaField.data.wsSelectDataSetUuid}`
          ];

        if (!foundDataset) return value;

        return (
          foundDataset.find((datasetItem) => datasetItem.value === value)
            ?.name ?? null
        );
      }
      return (
        metaField?.data?.singleSelectOptions?.find(
          (item: any) => item.value === value,
        )?.name ?? null
      );
    }
    if (metaField?.type === MetaFieldType.MultiSelect) {
      const valuesArr = metaField?.data?.singleSelectOptions?.filter(
        (item: any) => value?.includes(item.value),
      );

      if (valuesArr?.length) {
        return valuesArr.map((item: any) => item.name).join(", ");
      }

      return null;
    }
    if (metaField?.type === MetaFieldType.Member) {
      const foundMember = membersList?.find((member) => member.id === value);
      return foundMember?.name ?? null;
    }
    if (metaField?.type === MetaFieldType.Date) {
      if (value && value?.date) {
        const formattedValue = getDateLabel({
          lang: "en-EN",
          date: moment(value.date),
          disableRedLabel: false,
          intl,
        });

        return typeof formattedValue?.date === "string"
          ? formattedValue?.date
          : getFullDate(formattedValue?.date.toDate());
      }

      return null;
    }
    if (metaField?.type === MetaFieldType.Percent) {
      return value ? `${splitPercents(value)}%` : null;
    }
    if (metaField?.type === MetaFieldType.Number) {
      return value ? `${splitNumbers(value)}` : null;
    }
    if (metaField?.type === MetaFieldType.Currency) {
      return value && metaField?.data?.currencyCode
        ? `${metaField?.data?.currencyCode} ${splitNumbers(value)}`
        : null;
    }

    return value;
  };

  const filterOption = (option: any, input: string) => {
    const searchWords = input?.trim()
      ? input
          .trim()
          .split(" ")
          ?.map((substring: string) => substring.toUpperCase())
      : [];

    if (searchWords && searchWords.length > 0) {
      const concatString = `${option?.data?.name} ${option?.data?.subField ?? ""}`;
      const filteredName = searchWords
        ?.filter((word) => word !== "")
        .every((word) => concatString.trim().toUpperCase().includes(word));

      return option.data.__isNew__ || filteredName;
    }

    return true;
  };

  const generateOption = (listItem: Row) => {
    const icon = getDictionaryIcon(identity, wsDictionaryUuid);
    return {
      value: listItem.uuid,
      label: (
        <DictionaryElementLabel
          listItem={listItem}
          wsDictionarySubFieldUuid={wsDictionarySubFieldUuid}
          subMetaFieldValue={getSubMetaFieldValue(listItem)}
          icon={icon}
        />
      ),
      tableLabel: (
        <DictionaryElementTableLabel title={listItem.title} icon={icon} />
      ),
      name: listItem.title,
      subField: getSubMetaFieldValue(listItem) ?? "",
    };
  };

  const getMissingDataSet = (dataSetTypeUuid: string) => {
    if (!selectDataSetList[`dataSetType_${dataSetTypeUuid}`]) {
      dispatch(getSelectDataSetOptions(dataSetTypeUuid));
    }
  };

  useEffect(() => {
    if (
      wsDictionaryElementsFilter &&
      (wsDictionaryElementsFilter.wsDictionaryMetaFieldTargetUuid ||
        wsDictionaryElementsFilter.wsTaskMetaFieldUuid)
    )
      return;

    if (isLoading) return;

    const list =
      dictionaryAutoCompletes[`dictionaryType_${wsDictionaryUuid}`] || [];

    if (!list.length) return;

    const metaField = dictionaryAutoCompletesColumns[
      `dictionaryType_${wsDictionaryUuid}`
    ]?.find((item) => item.uuid === wsDictionarySubFieldUuid);

    if (metaField && typeof metaField.data.wsSelectDataSetUuid === "string") {
      getMissingDataSet(metaField.data.wsSelectDataSetUuid);
    }

    const generatedOptions = list.map(generateOption);
    setOptions(generatedOptions);
  }, [
    wsDictionaryElementsFilter,
    wsDictionaryUuid,
    wsDictionarySubFieldUuid,
    identity,
    dictionaryAutoCompletes,
    selectDataSetList,
    isLoading,
  ]);

  useEffect(() => {
    if (!foundCurrentMetaField || !wsDictionaryUuid) return;

    const getFilteredDictionaryList = async () => {
      setIsGettingFilteredDictionaryList(true);

      let metafieldFilterByUuid;
      switch (dataType) {
        case tableDataType.Dictionary:
          metafieldFilterByUuid =
            wsDictionaryElementsFilter?.wsDictionaryMetaFieldFilterByUuid;
          break;
        default:
          metafieldFilterByUuid =
            wsDictionaryElementsFilter?.wsDictionaryMetaFieldUuid;
          break;
      }

      if (!metafieldFilterByUuid) {
        console.error("No metafield filter by uuid");
        return;
      }

      try {
        const url = API_URLS.getDictionaryList.replace(
          ":dictionaryUuid:",
          wsDictionaryUuid,
        );

        let filterValue:
          | string
          | string[]
          | { from: string; to: string }
          | null = null;
        switch (foundCurrentMetaField.type) {
          case MetaFieldType.SingleSelect:
          case MetaFieldType.MultiSelect:
          case MetaFieldType.Member:
          case MetaFieldType.GlobalCreator:
            filterValue = [foundCurrentMetaField.value];
            break;
          case MetaFieldType.Percent:
          case MetaFieldType.Number:
            filterValue = {
              from: foundCurrentMetaField.value,
              to: foundCurrentMetaField.value,
            };
            break;
          default:
            filterValue = foundCurrentMetaField.value;
            break;
        }

        const response: AxiosResponse<{ content: DictionaryList }> =
          await axios.get(url, {
            params: {
              filters: {
                [metafieldFilterByUuid]: {
                  value: filterValue,
                },
              },
            },
          });
        if (response.status !== responseCodes["200_OK"]) {
          throw new Error(`Request failed with status code ${response.status}`);
        } else {
          const generatedOptions =
            response.data.content.list.map(generateOption);
          setOptions(generatedOptions);
        }
      } catch (error) {
        showErrorToast();
        console.error(error);
      } finally {
        setIsGettingFilteredDictionaryList(false);
      }
    };

    getFilteredDictionaryList();
  }, [
    wsDictionaryElementsFilter,
    wsDictionaryUuid,
    rowId,
    dataType,
    foundCurrentMetaField,
  ]);

  const showTooltip = wsDictionaryElementsFilter && selectedTasks.length > 1;

  return (
    <DropdownPortalMenu
      className="dictionary-element-selector-menu"
      onMouseLeave={(e) => e.stopPropagation()}
    >
      <Tooltip
        content={
          <IDHFormattedMessage
            id="ws_this_options_are_not_available_for_all_selected_items"
            defaultMessage="This options are not available for all selected items"
          />
        }
        contentHidden={!showTooltip}
        up
      >
        <CustomSelect
          options={isLoading ? skeletonOptions : options}
          value={selectedOption}
          onChange={handleChange}
          isSearchable
          menuIsOpen
          forceOpen={forceOpen}
          forceFocus={isVisible}
          filterOption={filterOption}
          onMenuClose={onMenuClose}
          openOnFocus={false}
          isOptionDisabled={() => showTooltip}
        />
      </Tooltip>
    </DropdownPortalMenu>
  );
}
